export default [
  {
    title: 'For landlords and family daycare operators',
    content: [
      {
        title: 'What do I need to do to comply with the Philadelphia lead law?',
        content: `
          <p><a href="https://www.phila.gov/services/permits-violations-licenses/apply-for-a-permit/environmental-permits-and-approvals/submit-lead-certification-and-inspection-reports/" target="_blank">Philadelphia law <i class="fas fa-external-link"></i></a> requires rental properties and family daycare facilities built after February 1978 to be certified as lead free or lead safe. Before obtaining a new or renewed rental or family daycare license, signing a lease of your property, or agreeing to rent to a tenant, you will need to submit information to this site to determine that your property is either in compliance with the regulations or exempt from them. You can start that process by returning to the <a href="https://leadcertification.phila.gov/" target="_blank">homepage</a>, searching for your property, and completing the questions that follow.</p>
          <p>If your property is required to be certified lead free or lead safe, a certified professional licensed to operate in Pennsylvania must perform an inspection to determine whether there is any lead paint contamination in the property. Learn more about inspection requirements and finding a qualified lead inspector <a href="https://www.phila.gov/services/permits-violations-licenses/apply-for-a-permit/environmental-permits-and-approvals/submit-lead-certification-and-inspection-reports/" target="_blank">here <i class="fas fa-external-link"></i></a>. When you receive your results from the technician, use this site to submit proof of certification to the City. You will need to provide a copy of the lead safe or lead free certificate for <strong>each unit</strong> on the property as well as documentation of the inspection and/or testing results. If an applicant doesn't have access to a computer, they should contact us at <a href="tel:(215) 685-2788">(215) 685-2788</a>.</p>
        `,
      },
    
      {
        title: 'How do I know if my property needs to be certified lead safe or lead free?',
        content: `
          <p>Complete the lead disclosure process for your property by <a href="https://leadcertification.phila.gov/" target="_blank">searching for your property</a> and answering the questions that follow. You will receive your result immediately. Learn about the difference between <a href="#link-1">lead free and lead safe certification</a> below.</p>
        `,
      },
      
      {
        title: 'How do I get my property certified lead safe or lead free?',
        content: `
          <p>There are many EPA certified companies and individuals that inspect properties and issue certificates. This list can be found on our list of <a href="https://www.phila.gov/documents/lead-and-healthy-homes-resources-for-landlords/" target="_blank">landlord resources <i class="fas fa-external-link"></i></a>.</p>
        `,
      },
    
      {
        title: 'Can I conduct the lead test myself?',
        content: `
          <p>There is no prohibition on a landlord or their staff conducting the inspection and dust wipe test, but the person performing the test must be an EPA-certified lead dust sampling technician or a PA certified risk assessor. Note that the do-it-yourself testing kits available from Home Depot or other local stores are not sufficient.</p>
        `,
      },
    
      {
        title: 'What do I do if an inspection shows lead contamination?',
        content: `
          <p>If the property fails visual, dust wipe, or XRF testing, the area must be corrected by a certified <a href="https://cfpub.epa.gov/flpp/pub/index.cfm?do=main.firmSearch" target="_blank">renovator <i class="fas fa-external-link"></i></a>. A technician must re-inspect the property after it has been treated to ensure that all surfaces are intact and re-take dust wipes of those failed areas.</p>
        `,
      },
    
      {
        title: 'Can I get assistance paying for lead abatement of my property(ies)?',
        content: `
          <p>Yes, there may be grants available through the Lead and Healthy Homes Program. <a href="./contact-us" target="_blank">Contact us</a> to learn more. You may also contact the <a href="https://phdcphila.org/community-investment/development-project-finance/rental-improvement-fund/" target="_blank">Philadelphia Housing Development Corporation <i class="fas fa-external-link"></i></a> to see loans that may be available in your zip code.</p>
        `,
      },
    
      {
        title: 'How long is a lead safe or lead free certification valid?',
        content: `
          <p>Lead safe certificates that were completed prior to January 31, 2019 are valid for two years. All certificates completed after February 1, 2020 are valid for four years. Lead free certificates do not expire.</p>
        `,
      },
    
      {
        title: 'What do I do if I own a building with multiple rental units?  Can I certify and rent them one at a time?',
        content: `
          <p>If the landlord is renting multiple units as part of one rental license, he or she must obtain a lead safe or lead free certificate for each unit and submit results for all units to the Lead and Healthy Homes Program before they will be able to renew the rental license for the property.</p>
        `,
      },
    
      {
        title: 'What do I do if I own a single condo unit within a multi-unit building?',
        content: `
          <p>To obtain a <strong>lead safe</strong> certificate, the rooms within the condo unit must pass the dust wipe inspection. To obtain a lead free certificate, the unit plus building common areas and hallways must test <strong>lead free</strong> using an XRF analyzer. If common areas and hallways do not pass inspection, please contact the certified renovator; repair the area, clean it and retest.</p>
        `,
      },
      
      {
        title: 'Are there any different requirements for family daycare facilities?',
        content: `
          <p>Family daycare facilities must test all common room areas accessible to children attending daycare. All other requirements are the same as residential rental certification.</p>
        `,
      },
    
      {
        title: 'What do I do if a tenant refuses entry to perform a lead test?',
        content: `
          <p>As a landlord, if the tenant refused to let you in to conduct a lead safe test, you must contact the tenant in writing via e-mail or postal mail. A copy of this documentation along with verification that the tenant received it must be submitted to the Health Department.</p>
        `,
      },
    
      {
        title: 'What documentation do I need to provide a new tenant?',
        content: `
          <p>The landlord must provide a copy of the current lead free or lead safe certificate to the tenant.</p>
        `,
      },
    
      {
        title: 'What are the penalties if I am not in compliance?',
        content: `
          <p>The owner will not be able to renew the rental license without a valid lead safe or lead free certificate. The Health Department will proceed with legal action which may include code violations, Notices of Violation, and issuance of fines. Property owners may also be prohibited from evicting the tenant while this matter is pending. Contact a legal advisor for further information about liabilities under the law</p>
        `,
      },
    
      {
        title: 'Do I still need to get my property certified if a child under 6 will be living there?',
        content: `
          <p>Regardless of the zip code, landlords must test and certify rental properties as lead safe or lead free if a child age 6 or under lives in the property, unless the property was built after 1978.</p>
        `,
      },
    
      {
        title: 'What is the city’s position on homes that were demolished and rebuilt under L&I codes. Is this considered new construction?',
        content: `
          <p>Even if the property was re-built and remodeled, it still needs a lead free or lead safe certificate. If the property was completely built after February 1978, it is exempt.</p>
        `,
      },
    
      {
        title: 'How do I apply for a rental license?',
        content: `
          <p>Rental licenses are issued by the City’s Department of Licenses and Inspections. Learn about getting a license <a href="https://www.phila.gov/services/permits-violations-licenses/get-a-license/business-licenses-permits-and-approvals/real-estate/get-a-rental-license/" target="_blank">here <i class="fas fa-external-link"></i></a>.</p>
        `,
      },
    ],
  },

  {
    title: 'For renters',
    content: [
      {
        title: 'How do I know that a rental property is safe from lead?',
        content: `
          If the property was built before March 1978, your landlord is required to provide you with a certificate of lead-safe or lead-free status, based on this <a href="https://www.phila.gov/2019-10-22-rental-property-lead-certification-law/" target="_blank">phase-in schedule <i class="fas fa-external-link"></i></a> by zip code. You can check the estimated age of your building at <a href="https://property.phila.gov/" target="_blank">property.phila.gov <i class="fas fa-external-link"></i></a>. Most properties in Philadelphia were built before 1978.
        `,
      },
      {
        title: 'What do I do if my landlord has not certified the property or if the certification has expired?',
        content: `
          <p>Please contact a legal advisor and report it to 311 or the <a href="https://www.phila.gov/programs/lead-and-healthy-homes-program/" target="_blank">Lead and Healthy Homes Program <i class="fas fa-external-link"></i></a>.</p>
        `,
      },
      {
        title: 'What if I don’t want an inspector to come to my house?',
        content: `
          <p>It is important to allow your landlord to have a qualified professional inspect the property and test for lead contamination. Your landlord should give you at least 5 days notice. You can choose to be present throughout the inspection or leave and come back later. Technicians are trained in safe and sanitary practices. If you witness or experience misconduct by a technician, contact your landlord and/or a legal advisor.</p>
        `,
      },
      {
        title: 'What should I do to protect my children from lead poisoning?',
        content: `
          <p>All children age six or under should be tested for lead by a doctor. Make sure kids wash their hands after playing outside and be aware of peeling or chipping paint. Learn more at our list of <a href="https://www.phila.gov/documents/lead-and-healthy-homes-resources-for-tenants/" target="_blank">tenant resources <i class="fas fa-external-link"></i></a>.</p>
        `,
      },
    ],
  },

  {
    title: 'Using the Lead Certification Submission System',
    content: [
      {
        title: 'I registered for an account but I can’t login.',
        content: `
          <p>Make sure you <a href="https://leadcertification.phila.gov/register/" target="_blank">register for an account</a> using an email address you can access. You will receive a confirmation email from the Lead and Healthy Homes Program with a link to activate your account. You must click the confirmation link in the email before you will be able to login. You should receive this email within a few minutes or up to an hour. If you don’t receive the email, be sure to check your spam folder for a message from no-reply@phila.gov. Try using the “<a href="https://leadcertification.phila.gov/reset-password/" target="_blank">Forgot password</a>” function if you still can’t find it.</p>
        `,
      },
      {
        title: 'I forgot what email address I used to login.',
        content: `
          <p>Unfortunately LHHP cannot disclose the email addresses of our users. Try to reset the password for any email address you may have used. If you registered with that email address you will receive an email from LHHP to reset your password. If you cannot identify the email address you used, you will need to create a new account.</p>
        `,
      },
      {
        title: 'I submitted my property’s information and the site says I am not required to submit a certification. Do I need to do anything else?',
        content: `
          <p>You are all set. Thanks for your help protecting kids from lead poisoning in Philadelphia.</p>
        `,
      },
      {
        title: 'I submitted my property’s information but eCLIPSE is still blocking my license application.',
        content: `
          <ul>
            <li>Make sure that you have submitted certifications for each unit on the property individually, even if the units were all inspected at the same time or their results were provided to you in a combined report. The number of Lead certifications listed on the property disclosure page should be the same as the number of units on the property’s rental license and certificate of occupancy. If there are many units on the property this may take some time, but it is required to ensure every unit is in compliance.</li>
            <li>Check the status of each certification for the property to ensure that none of the inspections failed or are currently expired.</li>
            <li>Make sure you submitted certifications under the correct OPA account number. This is listed for the rental license in <a href="https://eclipse.phila.gov/" target="_blank">eCLIPSE <i class="fas fa-external-link"></i></a>.</li>
            <li>If you see an error message in eCLIPSE that says “Could not validate Lead and Healthy Homes Certification due to missing OPA account on associated address”, <a href="https://www.phila.gov/services/property-lots-housing/submit-a-service-request-with-311/" target="_blank">contact 311 <i class="fas fa-external-link"></i></a> and report an issue with Licenses & Inspections - eCLIPSE. Include the address for your property</li>
          </ul>
        `,
      },
      {
        title: 'I submitted the wrong information for my property, can I change it?',
        content: `
          <p>You may withdraw an incomplete disclosure but cannot edit it. You can create a new disclosure for the property, however.</p>
        `,
      },
      {
        title: 'I have a rental unit(s) and a family daycare at the same property. Do I need to submit my information twice?',
        content: `
          <p>Yes, please submit separate disclosures for the property, one as a rental property and one as a family daycare facility.</p>
        `,
      },
      {
        title: 'I added my certification(s) for my property and it says that the dust wipe tests failed. What do I do now?',
        content: `
          <p>If you received a lead-free or lead-safe certificate from the technician, they should have verified that each of the results were less than the allowable threshold. Compare the results entered against the report to confirm that they were entered correctly, and if there were no errors, contact the firm that provided the certificate. If your inspection showed lead contamination above the allowed threshold, you will need to remediate the affected areas. Learn more about finding a qualified contractor who can perform abatement for lead-free or lead-safe certification under <a href="#link-2">“Who can safely repair peeling paint or address other problems found on a lead inspection?”</a>.</p>
        `,
      },
      {
        title: 'Can I create accounts for my staff and split up information to submit?',
        content: `
          <p>All certifications for a property must be submitted by the same account under the same disclosure in order to be recognized as compliant.</p>
        `,
      },
      {
        title: 'How do I change my account information?',
        content: `
          <p>You can update your <a href="https://leadcertification.phila.gov/profile" target="_blank">account information</a> at any time. However, the email address used to login cannot be changed.</p>
        `,
      },
    ],
  },

  {
    title: 'About the Lead Disclosure Obligation laws',
    content: [
      {
        title: 'Where can I read about the City of Philadelphia’s lead laws?',
        content: `
          <p>You can find the entire bill at <a href="http://phlcouncil.com/leadcert" target="_blank">http://phlcouncil.com/leadcert <i class="fas fa-external-link"></i></a>.</p>
        `,
      },
      {
        title: 'When does the new law take effect?',
        content: `
          <p>Starting October 1, 2020, landlords operating rental properties in eleven zip codes - regardless of if there are children under six in the property - will be required to obtain a lead certificate in order to obtain or renew a rental license, before signing or renewing a lease, or before accepting rental payment for a property. The remaining zip codes will be enforced over a two-year period. Those that fall into the remaining zip codes as of October 1 will continue to be subject to the current lead law requirement to hold a valid certificate for properties with children six and under.</p>
        `,
      },
      {
        title: 'What were the laws before the passage of Bill #180936?',
        content: `
          <p>Since 2012, Philadelphia law has required landlords to have properties that were built before March 1978 tested and certified as “lead-free” or “lead-safe” if there is a child age six or under residing in the property. Student housing and housing owned or subsidized by the Philadelphia Housing Authority or privately owned but currently leased under the Housing Choice Voucher Program were exempted from the law.</p>
        `,
      },
      {
        title: 'Are any rental properties exempt?',
        content: `
          <p>Once a zip code is phased into the law, the new ordinance applies to any property built before February 1978 except for college and university housing and other educational housing that is exclusively for students where non-family members are not permitted to reside. Hotels that obtain rental licenses are also not subject to this law.</p>
        `,
      },
      {
        title: 'What are the deadlines for properties by zip codes?',
        content: `
          <p>See the map and list of zip codes <a href="https://www.phila.gov/2019-10-22-rental-property-lead-certification-law/" target="_blank">here <i class="fas fa-external-link"></i></a>.</p>
        `,
      },
      {
        title: 'What if a child under 6 lives in the property?',
        content: `
          <p>Regardless of the zip code, landlords must test and certify rental properties as lead-safe or lead-free if a child age 6 or under lives in a property built prior to March 1978.</p>
        `,
      },
      {
        anchor: 'link-1',
        title: 'What is the difference between “lead-safe” and “lead-free” certification?',
        content: `
          <ul>
            <li><strong>Lead-safe</strong> certification means that a certified lead dust sampling technician or a licensed lead inspector-risk assessor has: 1) determined the property is free of deteriorated, flaking, chipping, peeling, chalking or not-intact paint, and 2) interior dust samples were collected, tested, and found not to contain hazardous levels of lead-contaminated dust. Beginning February 1, 2020, completed lead-safe certifications are good for four years</li>
            <li><strong>Lead-free</strong> certification means that a licensed lead risk assessor has certified that it does not have any lead-based paint based on EPA/HUD standards. These certificates are valid indefinitely.</li>
          </ul>
        `,
      },
      {
        title: 'Who can test for lead paint?',
        content: `
          <p>A PA-certified Risk Assessor or EPA-certified Lead Dust Sampling Technician must do the lead testing. A Lead Dust Sampling Technician (LDST) or PA-Certified Risk Assessor can provide a lead-safe certificate. Only a PA-certified lead inspector or risk assessor may issue a lead-free certificate. A list of qualified testing firms and professionals is <a href="https://www.phila.gov/documents/lead-and-healthy-homes-resources-for-landlords/" target="_blank">available <i class="fas fa-external-link"></i></a> from the EPA and the PA Department of Health.</p>
        `,
      },
      {
        anchor: 'link-2',
        title: 'Who can safely repair peeling paint or address other problems found on a lead inspection?',
        content: `
          <ul>
            <li>The EPA website has a list of certified Renovation Repair and Painting (RRP) firms: <a href="https://www.epa.gov/lead/renovation-repair-and-painting-program-contractors" target="_blank">https://www.epa.gov/lead/renovation-repair-and-painting-program-contractors <i class="fas fa-external-link"></i></a>.</li>
            <li>You may also visit the EPA website at: <a href="https://cfpub.epa.gov/flpp/pub/index.cfm?do=main.firmSearch" target="_blank">https://cfpub.epa.gov/flpp/pub/index.cfm?do=main.firmSearch <i class="fas fa-external-link"></i></a>.</li>
            <li>The landlord must ask the contractor to provide their RRP certification and/or PA license before hiring.</li>
          </ul>
        `,
      },
      {
        title: 'Why is lead paint testing important?',
        content: `
          <p>Exposure to even small amounts of lead in childhood can cause lifelong learning and behavior problems. The purpose of the law is to prevent children from becoming lead poisoned, and to ensure they live in lead-free or lead-safe housing by requiring landlords to certify a property is lead-safe or lead-free. Despite years of progress, each year significant numbers of children in Philadelphia suffer the irreparable harm of lead poisoning because of exposure to deteriorated lead paint and lead dust in their homes. More than half of these children are living with their families in rental units. This new law recognizes that even if young children do not reside at an address at that time, they may visit or live there in the future, and therefore every rental property must be safe.</p>
        `,
      },
    ],
  },
  {
    title: 'Signing in to the Lead Certification Submission System',
    content: [
      {
        title: 'I’ve been to this site before, but the login page was different. What changed?',
        content: `<p>This website is now participating in <a href="https://login.phila.gov" target="_blank">login.phila.gov <i class="fas fa-external-link"></i></a>. This is a new, more secure initiative that makes it easier to  sign in to City of Philadelphia applications.</p>`,
      },
      {
        title: 'How does login.phila.gov work?',
        content: `
          <p>Participating applications use login.phila.gov to help protect their users. Your login.phila.gov account will work with any participating application. Some applications, like this one, require extra information to complete your profile.</p>
        `,
      },
    ],
  },
];
